/* ═══════════════════════════════════════════════════════════════
   ONE DIFF BRAIN — block-level and word-level, pure.

   Lifted verbatim out of `renderer/services/specService.ts` (Phase 3,
   docs/MASTERS_UNIFIED_PLAN.md) so the SAME comparison serves:

     · the Studio's Track Changes view (draft vs published)
     · the issuance addendum summary (revision marks)
     · the master → variant update feed (`shared/variant-updates.ts`),
       which must also run outside the renderer

   Zero I/O. Since SYNC_PLAN D5 both halves ride the ONE engine: words via
   jsdiff, block adds/removes via `sync/merge3` (two-way = base and ours
   both the old blocks).
   `specService` re-exports every name below, so nothing that imported
   these from there had to change.
   ═══════════════════════════════════════════════════════════════ */

import { diffArrays } from 'diff';
import type { ContentBlock } from './spec';
import { merge3 } from './sync/merge3';

export interface DiffToken {
  value: string;
  op: 'keep' | 'add' | 'del';
}

/** Token-level word diff between two text strings (jsdiff `diffArrays` —
 *  SYNC_PLAN D5, no hand-built LCS). Whitespace is kept as separate tokens
 *  so we don't mangle spacing in the rendered diff. */
export function diffWords(oldText: string, newText: string): DiffToken[] {
  const tokenize = (s: string): string[] => s.split(/(\s+)/).filter((x) => x.length > 0);
  const out: DiffToken[] = [];
  for (const part of diffArrays(tokenize(oldText || ''), tokenize(newText || ''))) {
    const op: DiffToken['op'] = part.added ? 'add' : part.removed ? 'del' : 'keep';
    for (const value of part.value) out.push({ value, op });
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════
   INLINE DIFF — Interleaved old+new blocks for track-changes rendering.
   Walks the new blocks in order, splicing in removed-from-old blocks at the
   positions they previously occupied so the rendered diff reads like the old
   document with tracked changes, not "here are additions and here are deletions
   in separate piles."
   ═══════════════════════════════════════════════════════════════ */

export type InlineDiffKind = 'unchanged' | 'added' | 'removed' | 'modified';

export interface InlineDiffBlock {
  kind: InlineDiffKind;
  id: string;
  level: number;
  oldText?: string;
  newText?: string;
  oldLevel?: number;
  newLevel?: number;
  wordDiff?: DiffToken[];
  blockType?: string;
  imageData?: string;
}

export function buildInlineDiff(
  oldBlocks: ContentBlock[],
  newBlocks: ContentBlock[]
): InlineDiffBlock[] {
  const oldIdx = new Map(oldBlocks.map((b, i) => [b.id, i]));
  // Two-way through the sync engine: base = ours = old, theirs = new.
  const removedIds = new Set(
    merge3(oldBlocks, oldBlocks, newBlocks)
      .filter((c) => c.whole && c.kind === 'removed')
      .map((c) => c.blockId)
  );
  const out: InlineDiffBlock[] = [];
  let lastOldIdxEmitted = -1;

  const pushRemoved = (b: ContentBlock): void => {
    out.push({
      kind: 'removed',
      id: b.id,
      level: b.level,
      oldText: b.text,
      oldLevel: b.level,
      blockType: b.blockType,
      imageData: b.imageData,
    });
  };

  for (const nb of newBlocks) {
    const oIdxForNb = oldIdx.get(nb.id);
    if (oIdxForNb !== undefined) {
      // Splice in any removed blocks that lived between the last anchor and this one
      for (let k = lastOldIdxEmitted + 1; k < oIdxForNb; k++) {
        const rb = oldBlocks[k];
        if (removedIds.has(rb.id)) pushRemoved(rb);
      }
      lastOldIdxEmitted = oIdxForNb;

      const ob = oldBlocks[oIdxForNb];
      if (ob.text !== nb.text || ob.level !== nb.level) {
        out.push({
          kind: 'modified',
          id: nb.id,
          level: nb.level,
          oldText: ob.text,
          newText: nb.text,
          oldLevel: ob.level,
          newLevel: nb.level,
          wordDiff: diffWords(ob.text, nb.text),
          blockType: nb.blockType,
          imageData: nb.imageData,
        });
      } else {
        out.push({
          kind: 'unchanged',
          id: nb.id,
          level: nb.level,
          oldText: ob.text,
          newText: nb.text,
          blockType: nb.blockType,
          imageData: nb.imageData,
        });
      }
    } else {
      // Added
      out.push({
        kind: 'added',
        id: nb.id,
        level: nb.level,
        newText: nb.text,
        newLevel: nb.level,
        blockType: nb.blockType,
        imageData: nb.imageData,
      });
    }
  }

  // Tail: removed blocks that lived after the last surviving new block
  for (let k = lastOldIdxEmitted + 1; k < oldBlocks.length; k++) {
    const rb = oldBlocks[k];
    if (removedIds.has(rb.id)) pushRemoved(rb);
  }

  return out;
}
